import { useDialogStore } from "@eventr/common-storybook";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { useCallback } from "react";
import i18n from "i18n/i18next";

/** Asks whether to leave a screen with unsaved changes. Resolves true to leave. */
export function confirmDiscardUnsavedChanges(): Promise<boolean> {
  const { replaceDialog, clearDialog } = useDialogStore.getState();
  return new Promise((resolve) => {
    replaceDialog({
      kind: "confirm",
      text: i18n.t("edit-itinerary.item.unsaved-booking-text"),
      header: i18n.t("edit-itinerary.item.unsaved-booking"),
      onCancel: () => {
        clearDialog();
        resolve(false);
      },
      onConfirm: () => {
        clearDialog();
        resolve(true);
      },
      cancelText: i18n.t("general.cancel"),
      confirmText: i18n.t("general.leave"),
      confirmBtnStyle: "destructiveBorderless",
      backdropClosable: false,
    });
  });
}

/**
 * The unsaved-changes prompt a pushed or sheet screen guards its exit with. backButtonAction
 * covers the back chevron and swipe-back, onDismissAttempt a sheet's swipe-down.
 */
export function useUnsavedChangesGuard(
  hasUncommittedEdit: boolean,
  leave: () => void,
): {
  backButtonAction: () => void;
  backMayRefuse: boolean;
  cancellable: boolean;
  onDismissAttempt: () => void;
} {
  const confirmDiscard = useCallback((onConfirm: () => void) => {
    void confirmDiscardUnsavedChanges().then((leave) => leave && onConfirm());
  }, []);

  const backButtonAction = useCallback(() => {
    if (!hasUncommittedEdit) {
      leave();
      return;
    }
    confirmDiscard(leave);
  }, [hasUncommittedEdit, leave, confirmDiscard]);

  const nativeViewContext = useNativeNavigationViewContext();
  const onDismissAttempt = useCallback(() => {
    if (!hasUncommittedEdit) {
      nativeViewContext.dismiss();
      return;
    }
    confirmDiscard(() => nativeViewContext.dismiss());
  }, [hasUncommittedEdit, nativeViewContext, confirmDiscard]);

  return {
    backButtonAction,
    // When clean, back is a normal native pop (finger-tracking). When dirty, back routes
    // through backButtonAction so the confirm dialog can refuse it.
    backMayRefuse: hasUncommittedEdit,
    cancellable: !hasUncommittedEdit,
    onDismissAttempt,
  };
}
